import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";
import { useAgendamentoPorId } from "../../../hooks/queries/agendamento/useAgendamentoPorId/useAgendamentoPorId";
import { useAtualizarEnderecoAtendimento } from "../../../hooks/queries/agendamento/useAtualizarEnderecoAtendimento/useAtualizarEnderecoAtendimento";
import { useUfs } from "../../../hooks/queries/ibge/useUfs/useUfs";
import { useCidadesPorEstado } from "../../../hooks/queries/ibge/useCidadesPorEstado/useCidadesPorEstado";
import { useBuscarCep } from "../../../hooks/queries/viaCep/useBuscarCep/useBuscarCep";
import { IAtendimentoDomiciliarForm } from "../../../types/agendamento";
import { Tracking } from "../../../services/Tracking";

const schema = z.object({
  nome: z.string().min(1, "Você precisa informar o nome"),
  telefone: z.string().min(1, "Você precisa informar o telefone"),
  endereco: z.object({
    cep: z.string().min(1, "Você precisa informar o CEP"),
    logradouro: z.string().min(1, "Você precisa informar o logradouro"),
    numero: z.string().min(1, "Você precisa informar o número"),
    complemento: z.string().optional(),
    bairro: z.string().min(1, "Você precisa informar o bairro"),
    uf: z.string().min(1, "Você precisa informar a UF"),
    cidade: z.string().min(1, "Você precisa informar a cidade"),
  }),
});

export const useChangeServiceAddress = () => {
  const { uuidAgendamento } = useParams();
  const navigate = useNavigate();

  const { data: agendamento } = useAgendamentoPorId(uuidAgendamento ?? "");
  const { data: ufs } = useUfs();
  const { mutate: atualizarEndereco, isPending } =
    useAtualizarEnderecoAtendimento();
  const { buscarCep } = useBuscarCep();

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<IAtendimentoDomiciliarForm>({
    defaultValues: {
      nome: "",
      telefone: "",
      endereco: {
        cep: "",
        logradouro: "",
        numero: "",
        complemento: "",
        bairro: "",
        uf: "",
        cidade: "",
      },
    },
    resolver: zodResolver(schema),
  });

  const ufSelecionada = watch("endereco.uf");
  const { data: cidades } = useCidadesPorEstado(ufSelecionada ?? "");

  useEffect(() => {
    if (!agendamento?.atendimentoDomiciliar) return;

    reset({
      nome: agendamento.atendimentoDomiciliar.nome,
      telefone: agendamento.atendimentoDomiciliar.telefone,
      endereco: {
        cep: agendamento.atendimentoDomiciliar.endereco?.cep ?? "",
        logradouro: agendamento.atendimentoDomiciliar.endereco?.logradouro ?? "",
        numero: agendamento.atendimentoDomiciliar.endereco?.numero ?? "",
        complemento:
          agendamento.atendimentoDomiciliar.endereco?.complemento ?? "",
        bairro: agendamento.atendimentoDomiciliar.endereco?.bairro ?? "",
        uf: agendamento.atendimentoDomiciliar.endereco?.uf ?? "",
        cidade: agendamento.atendimentoDomiciliar.endereco?.cidade ?? "",
      },
    });
  }, [agendamento, reset]);

  const ufOptions = (ufs ?? []).map((uf) => ({
    value: uf.sigla,
    label: uf.sigla,
  }));

  const cidadeOptions = (cidades ?? []).map((cidade) => ({
    value: cidade.nome,
    label: cidade.nome,
  }));

  async function handleCepBlur(cep: string) {
    if (!cep) return;

    try {
      const endereco = await buscarCep(cep);

      setValue("endereco.logradouro", endereco.logradouro);
      setValue("endereco.bairro", endereco.bairro);
      setValue("endereco.cidade", endereco.localidade);
      setValue("endereco.uf", endereco.uf);
    } catch {
      toast.error("Cep não encontrado");
    }
  }

  function handleUfChange() {
    setValue("endereco.cidade", "");
  }

  function onSubmit(data: IAtendimentoDomiciliarForm) {
    if (!uuidAgendamento) return;

    Tracking.userAction(
      "mapa:agendamento/alterar-endereco:clique-botao-avancar",
    );

    atualizarEndereco(
      { ...data, uuid: uuidAgendamento },
      {
        onSuccess: () => {
          toast.success("Endereço de atendimento atualizado com sucesso!");
          navigate(`/meus-agendamentos/agendamento?id=${uuidAgendamento}`);
        },
        onError: ({
          response: {
            data: { mensagem },
          },
        }: any) => {
          Tracking.error(
            "mapa:agendamento/alterar-endereco:atualizar-endereco-falhou",
            { mensagem },
          );
        },
      },
    );
  }

  return {
    register,
    control,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    watch,
    ufOptions,
    cidadeOptions,
    handleCepBlur,
    handleUfChange,
    isSubmitting: isPending,
  };
};
